import cx from 'classnames'
import debounce from 'lodash.debounce'
import PubSub from 'pubsub-js'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import User from '@js/common/user'

import { Form } from '@f/elements/HookForm'

import { $TSFixMe } from '@f-fertighaus/applications/UserAccount/interfaces/utils'

import { DEBOUNCE_TIMEOUT_MS, FALLBACK_TIMEOUT_MS } from './constants'
import {
  AddressComponents,
  AddressComponentsGoogleMaps,
  ComponentFieldMap,
  FetchLocationDataBase,
  PlaceDetailsResponse,
  PlacePrediction,
  PredictionResponse,
  ResultData,
  Suggestion
} from './types'

const ENDPOINT_BASE_URL =
  DEPLOY_ENV === 'production'
    ? 'https://loc.data.fertighaus.de/'
    : 'https://neuraum-locations-staging.ey.r.appspot.com/'

export const AUTOCOMPLETE_ENDPOINT = `${ENDPOINT_BASE_URL}-/v1/maps/autocomplete`
export const PLACE_DETAILS_ENDPOINT = `${ENDPOINT_BASE_URL}-/v1/maps/place_details`

const mapPrediction = (prediction: PlacePrediction) => ({
  ...prediction,
  value: prediction.placeId,
  name: prediction.text.text
})

const fetchLocationData = async ({
  placeId,
  wtLocation,
  locationValue
}: FetchLocationDataBase): Promise<ResultData | undefined> => {
  return await fetch(`${PLACE_DETAILS_ENDPOINT}?place_id=${placeId}`)
    .then(r => r.json())
    .then(async (response: PlaceDetailsResponse) => {
      if (response.addressComponents) {
        window._wt({
          category: 'delivery-region',
          action: 'apply',
          values: [{ type: 'text', value: locationValue }],
          ...(wtLocation && { location: wtLocation })
        })

        const address_components = response.addressComponents

        const addressComponentsMap: ComponentFieldMap = {
          locality: ['longText', 'shortText'],
          postal_code: ['longText', 'shortText'],
          country: ['longText', 'shortText'],
          administrative_area_level_1: ['longText', 'shortText'],
          administrative_area_level_2: ['longText', 'shortText']
        }

        const composeAddress = (
          acc: ComponentFieldMap,
          component: google.maps.GeocoderAddressComponent
        ) => {
          const componentType = component.types[0]
          if (addressComponentsMap.hasOwnProperty(componentType)) {
            acc[componentType] = addressComponentsMap[componentType].reduce(
              (accFields, field) => {
                accFields[field] = component[field]
                return accFields
              },
              {}
            )
          }
          return acc
        }

        const address: AddressComponents = address_components?.reduce(
          composeAddress,
          {}
        )

        /* Check for missing properties */
        const incompleteKeys = Object.keys(addressComponentsMap).filter(key => {
          const fields = addressComponentsMap[key]
          return fields.some(field => !address[key]?.[field])
        })

        /* Call Geocode API to get missing properties – it's required to handle case when user types in BERLIN - place name without any postcode suggestion */
        if (incompleteKeys.length) {
          const geocoder = new google.maps.Geocoder()
          const { results: geoResults } = await geocoder.geocode({
            location: {
              lat: response.location.latitude,
              lng: response.location.longitude
            }
          })
          if (geoResults) {
            incompleteKeys.forEach(key => {
              const component = geoResults.find(
                (comp: google.maps.GeocoderResult) => comp.types[0] === key
              )
              if (component) {
                const addressData = component.address_components.find(t =>
                  t.types.includes(key)
                )
                if (addressData) {
                  const { types, ...addressDataWithoutTypesField } = addressData
                  const keyDictionary = {
                    long_name: 'longText',
                    short_name: 'shortText'
                  }

                  if (!address[key]) {
                    address[key] = {}
                  }

                  const normalizedAddressData = Object.keys(
                    addressDataWithoutTypesField
                  ).reduce((acc, field) => {
                    acc[keyDictionary[field]] =
                      addressDataWithoutTypesField[field]
                    return acc
                  }, {})

                  address[key] = normalizedAddressData
                }
              }
            })
          }
        }

        const city =
          address.locality?.longText ||
          address.locality?.shortText ||
          address.administrative_area_level_2?.longText ||
          address.administrative_area_level_2?.shortText ||
          address.administrative_area_level_1?.longText ||
          address.administrative_area_level_1?.shortText

        const data = {
          label: `${address.country?.shortText}-${address?.postal_code?.longText} ${city}`,
          city,
          postcode: `${address.country?.shortText}-${address?.postal_code?.longText}`,
          state: {
            short_name: address.administrative_area_level_1?.shortText,
            long_name: address.administrative_area_level_1?.longText
          },
          country: address.country?.longText,
          location: {
            lat: response.location.latitude,
            lng: response.location.longitude
          }
        }
        return data
      }
    })
}

const fallbackFetchLocationData = async ({
  placeId,
  wtLocation,
  locationValue,
  sessionToken,
  placesServiceRef,
  callback
}: FetchLocationDataBase & {
  sessionToken?: google.maps.places.AutocompleteSessionToken
  placesServiceRef?: google.maps.places.PlacesService
  callback: (data: ResultData) => void
}) => {
  if (placeId && placesServiceRef && sessionToken) {
    placesServiceRef.getDetails(
      {
        placeId: placeId,
        sessionToken: sessionToken,
        fields: ['address_components', 'geometry']
      },
      async (result, status) => {
        if (status === 'OK') {
          if (result && result.address_components) {
            window._wt({
              category: 'delivery-region',
              action: 'apply',
              values: [{ type: 'text', value: locationValue }],
              ...(wtLocation && { location: wtLocation })
            })

            const address_components = result.address_components

            const addressComponentsMap: ComponentFieldMap = {
              locality: ['long_name', 'short_name'],
              postal_code: ['long_name', 'short_name'],
              country: ['long_name', 'short_name'],
              administrative_area_level_1: ['long_name', 'short_name'],
              administrative_area_level_2: ['long_name', 'short_name']
            }

            const composeAddress = (
              acc: ComponentFieldMap,
              component: google.maps.GeocoderAddressComponent
            ) => {
              const componentType = component.types[0]
              if (addressComponentsMap.hasOwnProperty(componentType)) {
                acc[componentType] = addressComponentsMap[componentType].reduce(
                  (accFields, field) => {
                    accFields[field] = component[field]
                    return accFields
                  },
                  {}
                )
              }
              return acc
            }

            const address: AddressComponentsGoogleMaps = address_components?.reduce(
              composeAddress,
              {}
            )

            /* Check for missing properties */
            const incompleteKeys = Object.keys(addressComponentsMap).filter(
              key => {
                const fields = addressComponentsMap[key]
                return fields.some(field => !address[key]?.[field])
              }
            )

            /* Call Geocode API to get missing properties – it's required to handle case when user types in BERLIN - place name without any postcode suggestion */
            if (incompleteKeys.length) {
              const geocoder = new google.maps.Geocoder()
              const { results: geoResults } = await geocoder.geocode({
                location: result.geometry.location
              })

              if (geoResults) {
                incompleteKeys.forEach(key => {
                  const component = geoResults.find(
                    (comp: google.maps.GeocoderResult) => comp.types[0] === key
                  )
                  if (component) {
                    const addressData = component.address_components.find(t =>
                      t.types.includes(key)
                    )
                    if (addressData) {
                      const {
                        types,
                        ...addressDataWithoutTypesField
                      } = addressData
                      if (!address[key]) {
                        address[key] = {}
                      }

                      address[key] = addressDataWithoutTypesField
                    }
                  }
                })
              }
            }

            const city =
              address.locality?.long_name ||
              address.locality?.short_name ||
              address.administrative_area_level_2?.long_name ||
              address.administrative_area_level_2?.short_name ||
              address.administrative_area_level_1?.long_name ||
              address.administrative_area_level_1?.short_name

            const data = {
              label: `${address.country?.short_name}-${address?.postal_code?.long_name} ${city}`,
              city,
              postcode: `${address.country?.short_name}-${address?.postal_code?.long_name}`,
              state: {
                short_name: address.administrative_area_level_1?.short_name,
                long_name: address.administrative_area_level_1?.long_name
              },
              country: address.country?.long_name,
              location: {
                lat: result.geometry?.location?.lat(),
                lng: result.geometry?.location?.lng()
              }
            }

            callback(data)
          }
        }
      }
    )
  }
}

type RenderPredictionItem = ReturnType<typeof mapPrediction>

export const renderPrediction = (item: RenderPredictionItem) => {
  const { text } = item

  const description = text.text

  const offset = text.matches[0].endOffset

  const matchedDescription = description.substring(0, offset)
  const afterDescription = description.substring(offset, text.text.length)

  return (
    <>
      <strong>{matchedDescription}</strong>
      {afterDescription}
    </>
  ) as React.ReactNode
}

const debouncedGetPlacePredictions = debounce(
  ({
    autocompleteService,
    sessionToken,
    input,
    callback,
    errorCallback
  }: {
    autocompleteService: google.maps.places.AutocompleteService
    sessionToken: google.maps.places.AutocompleteSessionToken
    input: string
    callback: (predictions: google.maps.places.AutocompletePrediction[]) => void
    errorCallback: (error: Error) => void
  }) => {
    autocompleteService
      .getPlacePredictions(
        {
          input: input,
          types: ['(regions)'],
          componentRestrictions: { country: ['de', 'at'] },
          sessionToken: sessionToken
        },
        results => {
          return callback(results || [])
        }
      )
      .catch(e => errorCallback(e))
  },
  DEBOUNCE_TIMEOUT_MS
)

const normalizeToSuggestionStructure = (
  googleMapsPredictions: google.maps.places.AutocompletePrediction[]
): Suggestion[] => {
  return googleMapsPredictions.map(prediction => ({
    placePrediction: {
      place: `places/${prediction.place_id}`,
      placeId: prediction.place_id,
      text: {
        text: prediction.description,
        matches: [
          {
            endOffset: prediction.matched_substrings[0].offset
          }
        ]
      },
      structuredFormat: {
        mainText: {
          text: prediction.structured_formatting.main_text,
          matches: [
            {
              endOffset: prediction.matched_substrings[0].length
            }
          ]
        },
        secondaryText: {
          text: prediction.structured_formatting.secondary_text
        }
      },
      types: prediction.types
    }
  }))
}

export interface LocationFinderProps {
  placeholder?: string
  className?: string
  inputClassName?: string
  localData?: $TSFixMe | null
  onChange?: (value: $TSFixMe) => void
  shouldSaveAddress?: boolean
  forceRemove?: boolean
  wtLocation?: string
}

const LocationFinder = forwardRef(function LocationFinder(
  {
    placeholder = 'z.B. 12345 oder Berlin',
    className = '',
    inputClassName = '',
    onChange,
    localData = null,
    forceRemove = false,
    wtLocation = '',
    shouldSaveAddress = true
  }: LocationFinderProps,
  ref
) {
  const [useFallbackMechanism, setUseFallbackMechanism] = useState(false)
  const [currentLocation, setCurrentLocation] = useState(
    forceRemove ? {} : localData || User.getLocation()
  )

  const [
    sessionToken,
    setSessionToken
  ] = useState<google.maps.places.AutocompleteSessionToken | null>(null)
  const autoCompleteServiceRef = React.useRef<google.maps.places.AutocompleteService | null>(
    null
  )
  const placesServiceRef = React.useRef<google.maps.places.PlacesService | null>(
    null
  )
  const comboboxRef = React.useRef<{ resetCombobox: () => void }>()

  const [predictions, setPredictions] = useState<Suggestion[]>([])

  const predictionsMapped = predictions.map(prediction =>
    mapPrediction(prediction.placePrediction)
  )

  const methods = useForm({
    defaultValues: {
      location: currentLocation.label
    }
  })
  const { watch, getValues, resetField, setError, clearErrors } = methods

  const fallbackToGoogleMaps = useCallback(
    (locationInput: string) => {
      const token = new window.google.maps.places.AutocompleteSessionToken()
      setUseFallbackMechanism(true)
      setSessionToken(token)
      autoCompleteServiceRef.current = new window.google.maps.places.AutocompleteService()
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        document.createElement('div')
      )
      if (token && autoCompleteServiceRef.current) {
        debouncedGetPlacePredictions({
          autocompleteService: autoCompleteServiceRef.current,
          sessionToken: token,
          input: locationInput,
          callback: predictionsValue => {
            clearErrors('location')
            const normalizedPredictions = normalizeToSuggestionStructure(
              predictionsValue
            )
            setPredictions(normalizedPredictions)
          },
          errorCallback: () => {
            setError('location', {
              type: 'manual',
              message:
                'Ein Fehler ist aufgetreten, bitte versuchen Sie es später noch einmal.'
            })
          }
        })
      }
    },
    [clearErrors, setError]
  )

  const fetchPredictions = useCallback(
    debounce((input: string) => {
      let isFallbackTriggered = false

      // Start a 1-second timer for fallback
      const fallbackTimer = setTimeout(() => {
        isFallbackTriggered = true
        fallbackToGoogleMaps(input)
      }, FALLBACK_TIMEOUT_MS)

      fetch(`${AUTOCOMPLETE_ENDPOINT}?input=${input}`)
        .then(async r => {
          if (isFallbackTriggered) {
            return
          }
          clearTimeout(fallbackTimer)
          const data: PredictionResponse = await r.json()

          if (!isMounted.current) {
            return
          }
          if (data.suggestions) {
            setPredictions(data.suggestions)
          }
          // Add fallback to Google Maps API here if needed
        })
        .catch(() => {
          if (isFallbackTriggered) return
          if (!isMounted.current) return
          fallbackToGoogleMaps(input)
        })
        .finally(() => {
          if (isMounted.current) {
            clearTimeout(fallbackTimer)
          }
        })
    }, DEBOUNCE_TIMEOUT_MS),
    [] // Empty dependency array ensures this function is created only once
  )

  const isMounted = useRef(true)

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    return () => {
      fetchPredictions.cancel()
    }
  }, [fetchPredictions])

  useEffect(() => {
    const subscription = watch(values => {
      if (values.location && values.location.length >= 2) {
        fetchPredictions(values.location)
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, fetchPredictions])

  useEffect(() => {
    /* candidate to be removed as it's only used in RadiusSearch component which is used in service_model_homes and service_providers.
     */
    if (forceRemove && !localData && shouldSaveAddress) {
      User.setLocation({})
    }
  }, [forceRemove, shouldSaveAddress, localData])

  useImperativeHandle(
    ref,
    () => ({
      resetLocationInput() {
        setCurrentLocation({})
        resetField('location')
        comboboxRef.current?.resetCombobox()
      }
    }),
    [setCurrentLocation, resetField]
  )

  const handleSelect = (
    prediction: RenderPredictionItem,
    setInputValue: (value: string) => void
  ) => {
    setInputValue(prediction.name)

    const afterFetch = (data?: ResultData) => {
      if (shouldSaveAddress) {
        User.setLocation(data)
      }
      PubSub.publish('QUERYUPDATE', {
        field: 'delivery_regions__in',
        ...data
      })

      if (onChange) {
        onChange(data)
      }

      setPredictions([])
      setInputValue(data.label)
      setCurrentLocation(data)

      if (useFallbackMechanism) {
        // Reset token – it's important for billing purposes
        setSessionToken(
          new window.google.maps.places.AutocompleteSessionToken()
        )
        setUseFallbackMechanism(false)
      }
    }

    if (useFallbackMechanism) {
      fallbackFetchLocationData({
        placeId: prediction.value,
        wtLocation,
        locationValue: getValues('location'),
        sessionToken: sessionToken || '',
        placesServiceRef: placesServiceRef.current,
        callback: afterFetch
      })
    } else {
      fetchLocationData({
        placeId: prediction.value,
        wtLocation,
        locationValue: getValues('location')
      }).then(afterFetch)
    }
  }

  return (
    <span
      className={cx(
        'LocationFinder',
        className,
        currentLocation.label && 'has-bauort'
      )}>
      <FormProvider {...methods}>
        <Form.Combobox
          ref={comboboxRef}
          items={predictionsMapped}
          name='location'
          onItemSelect={handleSelect}
          renderSuggestion={renderPrediction}
          placeholder={placeholder}
          disabled={false}
          locationFinderPlaceholder={currentLocation.label}
          className={cx(
            'f-c-bo-neu025 FormText-wrapper has-border is-full-width',
            inputClassName
          )}
        />
      </FormProvider>
    </span>
  )
})

export default LocationFinder
